import { Divider, makeStyles } from '@material-ui/core';
import React from 'react';
import { Download } from 'react-feather';
import { usePrefixTranslation } from '../../../i18n';
import { actions, useTypedDispatch, useTypedSelector } from '../../../redux';
import { theme } from '../../../theme/theme';
import { enumToStatusType, probabilities } from '../../../types/common';
import { RiskAssessment } from '../../../types/plan';
import { downloadFileDialog, getContentUrl } from '../../../util/file';
import { Badge, Button, IconButton, Property, StatusCard } from '../../global';

const useCompletedClasses = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        '& > hr': {
            background: theme.colors.neutral[3],
            margin: '10px 0 20px 0',
        },
    },
    content: {
        display: 'flex',
        flexDirection: 'column',
        flex: '1 1 auto',
        height: 0,
        overflowX: 'hidden',
        overflowY: 'auto',
        '& > *': {
            margin: '10px 0',
        },
        '& > span': {
            color: theme.colors.darkBlue[5],
            ...theme.typography.headings.h5,
        },
    },
    document: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '12px 20px',
        marginBottom: 10,
        '& > div': {
            display: 'flex',
            flexDirection: 'column',
        },
        '& > div > span:first-child': {
            color: theme.colors.neutral[1],
            ...theme.typography.headings.h4,
            marginBottom: 0,
        },
        '& > div > span:last-child': {
            color: theme.colors.neutral[1],
            ...theme.typography.content.caption.paragraph,
        },
        '& > button': {
            color: theme.colors.neutral[3],
        },
    },
    button: {
        alignSelf: 'flex-end',
    },
});

/**
 * Editor for projects in the completed mode.
 *
 * @param props.close callback called when the user updates and closes the view
 */
export const Completed = (props: { onClose: () => void }) => {
    const classes = useCompletedClasses();
    const { t } = usePrefixTranslation(['project', 'common'], { project: 'editor.Completed' });
    const dispatch = useTypedDispatch();
    const { plan, project, users } = useTypedSelector(state => ({
        plan: state.plan.changed,
        project: state.project.changed,
        users: state.user.users,
    }));

    if (plan && !plan.risk_assessment)
        plan.risk_assessment = {
            id: -1,
            likelihood_risk: 'low',
            likelihood_description: '',
            consequence_risk: 'low',
            consequence_description: '',
        };

    React.useEffect(() => void dispatch(actions.user.get()), []);

    const updateRiskAssessment = (changes: Partial<RiskAssessment>) => {
        dispatch(actions.plan.edit({ risk_assessment: { ...plan?.risk_assessment!, ...changes } }));
    };

    if (!plan || !project) return <span>{'#plan or project not in store'}</span>;

    return (
        <div className={classes.root}>
            {project.report !== '' && (
                <StatusCard className={classes.document} type='light' large>
                    <div>
                        <span>{project.report}</span>
                        <span>
                            {t('report-update', {
                                context: users.find(u => u.id === project.user) ? 'user' : 'no-user',
                                name: users.find(u => u.id === project.user)?.full_name ?? 'missing user name',
                                date: project.under_review_date,
                            })}
                        </span>
                    </div>
                    <IconButton
                        size='small'
                        title='download'
                        onClick={async () => {
                            if (project.report === '') return;
                            const url = await getContentUrl(project.report);
                            downloadFileDialog(url, 'report');
                        }}
                    >
                        <Download />
                    </IconButton>
                </StatusCard>
            )}
            <div className={classes.content}>
                {(['likelihood', 'consequence'] as const).map(assessment => (
                    <React.Fragment key={assessment}>
                        <span>{t(`title-${assessment}`)}</span>
                        <Property
                            editable
                            name={t('input-probability')}
                            value={plan.risk_assessment![`${assessment}_risk` as keyof RiskAssessment]}
                            options={probabilities}
                            renderOption={option => (
                                <Badge style={{ display: 'inline-flex' }} type={enumToStatusType[option]}>
                                    {t(`common:probability.${option}`)}
                                </Badge>
                            )}
                            onChange={(_, value) => {
                                if (value == undefined) return;
                                updateRiskAssessment({ [`${assessment}_risk`]: value.toString() });
                            }}
                        />
                        <Property
                            editable
                            name={t('input-justification')}
                            multiline
                            rows={5}
                            value={plan.risk_assessment![`${assessment}_description` as keyof RiskAssessment]}
                            onChange={(_, value) =>
                                updateRiskAssessment({ [`${assessment}_description`]: value?.toString() ?? '' })
                            }
                        />
                    </React.Fragment>
                ))}
            </div>
            <Divider />
            <Button
                className={classes.button}
                variant='contained'
                onClick={async () => {
                    dispatch(actions.plan.commit());
                    dispatch(actions.project.commit());
                    await Promise.all([dispatch(actions.plan.update(true)), dispatch(actions.project.update(true))]) //
                        .catch(error => {});
                    props.onClose();
                }}
            >
                {t('button-update-close')}
            </Button>
        </div>
    );
};
